import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Coins,
  CreditCard,
  Loader2,
  LogIn,
  RefreshCw,
  ShieldCheck,
  Clock,
  RotateCcw,
  Wallet,
  ArrowRight,
  Lock,
  Zap,
  ArrowLeft,
  LayoutDashboard,
  HelpCircle,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { CreditPlansGrid } from '@/components/billing/CreditPlansGrid';
import { useCreditRecharge, syncUserCredits } from '@/lib/billing/useCreditRecharge';
import { useCreditPlans } from '@/lib/billing/useCreditPlans';
import { creditsForInr, formatInr } from '@/lib/billing/creditPlans';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const PRESET_AMOUNTS = [499, 999, 2499, 4999];

export function PricingPage({ isLoggedIn: isLoggedInProp } = {}) {
  const navigate = useNavigate();
  const isLoggedIn = isLoggedInProp ?? Boolean(localStorage.getItem('storycraft_token'));
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [amountInr, setAmountInr] = useState('999');
  const [walletLoading, setWalletLoading] = useState(isLoggedIn);

  const loadWallet = useCallback(async () => {
    if (!isLoggedIn) {
      setWalletLoading(false);
      return;
    }
    setWalletLoading(true);
    try {
      const [walletRes, txRes] = await Promise.all([
        api.get('/billing/wallet'),
        api.get('/billing/transactions', { params: { limit: 20 } }),
      ]);
      setWallet(walletRes.data);
      setTransactions(Array.isArray(txRes.data?.items) ? txRes.data.items : []);
      if (typeof walletRes.data?.balance === 'number') {
        syncUserCredits(walletRes.data.balance, walletRes.data);
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load wallet'));
    } finally {
      setWalletLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  const {
    plans,
    loading: plansLoading,
    refresh: refreshPlans,
    billingEnabled: publicBillingOn,
    creditsPerInr: plansCpi,
  } = useCreditPlans();

  const { startRecharge, payingPlanId, isPaying } = useCreditRecharge({
    onSuccess: () => {
      loadWallet();
      refreshPlans();
    },
  });

  const resolvedCreditsPerInr = wallet?.credits_per_inr ?? plansCpi ?? 10;
  const billingOn = isLoggedIn
    ? wallet?.billing_enabled !== false
    : publicBillingOn !== false;
  const customCredits = creditsForInr(amountInr, resolvedCreditsPerInr);
  const pageLoading = plansLoading || walletLoading;
  const location = useLocation();
  const { hash } = location;
  const unlockAnalytics = new URLSearchParams(location.search).get('unlock') === 'analytics';

  useEffect(() => {
    if (pageLoading) return;
    const targetId = hash ? decodeURIComponent(hash.slice(1)) : unlockAnalytics ? 'plan-creator' : '';
    if (!targetId) return;
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pageLoading, hash, unlockAnalytics]);

  const requireLogin = () => {
    toast.info('Log in to purchase credits');
    navigate('/login', { state: { from: '/pricing' } });
  };

  const handleBuyPlan = (plan) => {
    if (!isLoggedIn) {
      requireLogin();
      return;
    }
    startRecharge(plan);
  };

  const handleCustomRecharge = () => {
    if (!isLoggedIn) {
      requireLogin();
      return;
    }
    startRecharge(amountInr, 'custom-amount');
  };

  return (
    <div
      className="min-h-screen w-full bg-black text-white font-sora relative overflow-x-hidden selection:bg-[#2DD4BF]/30 flex flex-col justify-between"
      style={{ isolation: 'isolate' }}
      data-testid="pricing-page"
    >
      {/* ── Ambient glows pinned to corners (Exact AvatarCast / Narration style) ── */}
      <div
        className="fixed top-0 left-0 w-[700px] lg:w-[900px] pointer-events-none z-[1] select-none"
        style={{ opacity: 0.60, transform: 'translate(-20%, -20%)' }}
        dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }}
      />
      <div
        className="fixed bottom-0 right-0 w-[600px] lg:w-[800px] pointer-events-none z-[1] select-none"
        style={{ opacity: 0.60, transform: 'translate(20%, 20%)' }}
        dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }}
      />

      {/* ─────────────────────────────────────────
          Top Navigation Bar (AvatarCast / Narration Style)
      ───────────────────────────────────────── */}
      <header className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-5 pb-3 flex items-center justify-between gap-2 flex-wrap border-b border-white/5 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="h-8 px-3.5 rounded-xl border border-white/10 text-white hover:bg-white/[0.06] hover:text-[#62FAE3] text-xs font-bold flex items-center gap-2 cursor-pointer backdrop-blur-md bg-white/[0.02] transition-all shadow-sm font-jakarta"
            title="Return to Dashboard"
          >
            <LayoutDashboard className="w-4 h-4 text-[#62FAE3]" />
            <span>Dashboard</span>
          </button>

          <Link
            to="/"
            className="hidden sm:flex items-center hover:opacity-90 transition-opacity cursor-pointer"
            title="Twinnee Home"
          >
            <TwinneeWordmark className="h-8" />
          </Link>
        </div>

        <div className="flex items-center gap-3 font-jakarta">
          {isLoggedIn ? (
            <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-[#2DD4BF]/10 border border-[#2DD4BF]/25 shadow-sm">
              <Coins className="w-4 h-4 text-[#2DD4BF]" />
              <span className="text-xs font-mono font-bold text-[#62FAE3]">
                {(wallet?.balance ?? 0).toLocaleString('en-IN', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}{' '}
                Credits
              </span>
              <button
                type="button"
                onClick={loadWallet}
                disabled={walletLoading}
                className="ml-1 text-[#8a94a6] hover:text-white transition-colors cursor-pointer"
                title="Refresh Credits"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${walletLoading ? 'animate-spin text-[#2DD4BF]' : ''}`}
                />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/login', { state: { from: '/pricing' } })}
              className="h-8 px-4 rounded-xl border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 text-[#62FAE3] hover:bg-[#2DD4BF]/20 text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-all shadow-sm"
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>Log In</span>
            </button>
          )}

          <button
            onClick={() => navigate('/script-audio-align', { state: { prefill: { mode: 'topic' } } })}
            className="h-8 px-3 rounded-xl border border-white/10 text-[#8a94a6] hover:text-white hover:bg-white/[0.05] text-xs font-medium flex items-center gap-1.5 cursor-pointer transition-all"
            title="Help & Guides"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Help</span>
          </button>
        </div>
      </header>

      {/* ─────────────────────────────────────────
          Main Pricing Content Container
      ───────────────────────────────────────── */}
      <main className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 flex-1">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Header Title Section */}
          <div className="space-y-3 mb-8 sm:mb-12">
            <p className="text-[11px] font-mono font-medium tracking-[0.18em] text-[#2DD4BF] uppercase">
              PLANS & RECHARGE
            </p>

            <h1 className="text-3xl sm:text-4xl lg:text-[46px] font-extrabold tracking-tight text-white leading-tight">
              Power Your Creations
              <span
                className="block mt-1"
                style={{
                  background: 'linear-gradient(106.58deg, #D0BCFF 0%, #62FAE3 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                with Instant AI Credits
              </span>
            </h1>

            <p className="text-sm sm:text-[15px] text-[#9CA3AF] font-normal max-w-2xl font-jakarta leading-relaxed">
              Pay as you go for AI voiceover, high-definition video renders, character avatars, and custom music. Credits never expire and remain in your wallet until you generate.
            </p>
          </div>

          {/* Not Logged In Prompt Banner */}
          {!isLoggedIn ? (
            <div className="rounded-2xl p-4 sm:p-5 mb-8 border border-[#2DD4BF]/30 bg-[#2DD4BF]/[0.04] backdrop-blur-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 font-jakarta">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 flex items-center justify-center shrink-0">
                  <TwinneeLogo className="w-4 h-4 text-[#62FAE3]" />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-white">
                    Sign in to purchase & track credits in real-time
                  </h4>
                  <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                    Purchased credits attach permanently to your creator wallet.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate('/login', { state: { from: '/pricing' } })}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#2DD4BF] text-white font-bold text-xs shadow-md hover:opacity-95 transition-all shrink-0 cursor-pointer flex items-center justify-center gap-1.5"
              >
                <span>Log In to Buy</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : null}

          {pageLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#2DD4BF]" />
              <p className="text-xs font-mono text-[#9CA3AF]">Loading creative wallet & plans...</p>
            </div>
          ) : (
            <>
              {/* Wallet Overview Panel (Logged In) */}
              {isLoggedIn ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 sm:p-6 mb-10 shadow-xl font-jakarta">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#A78BFA]/20 to-[#2DD4BF]/20 border border-white/15 flex items-center justify-center shrink-0 shadow-inner">
                        <Wallet className="w-6 h-6 text-[#62FAE3]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-[#9CA3AF]">
                            Current Wallet Balance
                          </span>
                          <span className="w-2 h-2 rounded-full bg-[#34d399] animate-pulse" />
                          {wallet?.paid ? (
                            <span
                              className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#34d399] text-[#06211b]"
                              data-testid="account-paid-badge"
                            >
                              Paid
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-[#9CA3AF]">
                              Free
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-2 mt-0.5">
                          <span className="font-sora font-black text-3xl sm:text-4xl text-white tracking-tight">
                            {(wallet?.balance ?? 0).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          <span className="text-xs sm:text-sm font-bold text-[#62FAE3] font-mono uppercase">
                            Credits
                          </span>
                        </div>
                        {wallet?.balance_inr != null ? (
                          <p className="text-[11px] text-[#9CA3AF] mt-0.5 font-mono">
                            ≈ ₹{Number(wallet.balance_inr).toFixed(2)} INR · {resolvedCreditsPerInr} Credits / ₹1
                          </p>
                        ) : null}
                        <p className="text-sm font-semibold text-white mt-2" data-testid="current-plan-label">
                          Current plan: {wallet?.current_plan_name || (wallet?.paid ? 'Custom top-up' : 'None')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center">
                      <button
                        type="button"
                        onClick={loadWallet}
                        disabled={walletLoading}
                        className="px-4 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 hover:border-white/20 text-xs font-bold text-gray-200 transition-all flex items-center gap-1.5 cursor-pointer shadow-sm disabled:opacity-50"
                      >
                        <RefreshCw
                          className={`w-3.5 h-3.5 ${walletLoading ? 'animate-spin text-[#2DD4BF]' : ''}`}
                        />
                        <span>Sync Balance</span>
                      </button>
                    </div>
                  </div>

                  {!billingOn ? (
                    <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-xs text-amber-300 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 shrink-0" />
                      <span>Billing is currently in test mode or maintenance on this server.</span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* ─────────────────────────────────────────
                  Credit Plans Grid
              ───────────────────────────────────────── */}
              <div className="mb-12 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-mono font-medium tracking-[0.18em] text-[#9CA3AF] uppercase">
                    SELECT A CREDIT PACKAGE
                  </p>
                </div>
                {unlockAnalytics ? (
                  <div className="rounded-2xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-sm text-amber-50" data-testid="unlock-analytics-banner">
                    Any paid plan unlocks the pre-publish and ranking timeline.
                  </div>
                ) : null}

                <CreditPlansGrid
                  plans={plans}
                  creditsPerInr={resolvedCreditsPerInr}
                  billingEnabled={billingOn}
                  onBuyPlan={handleBuyPlan}
                  payingPlanId={payingPlanId}
                  currentPlanId={wallet?.current_plan_id || ''}
                  loading={plansLoading}
                />
              </div>

              {/* ─────────────────────────────────────────
                  Custom Recharge Section (Figma Exact Control Box)
              ───────────────────────────────────────── */}
              {billingOn && isLoggedIn ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 sm:p-6 mb-12 shadow-xl font-jakarta">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 rounded-lg bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 flex items-center justify-center">
                      <CreditCard className="w-3.5 h-3.5 text-[#62FAE3]" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold font-sora text-white">
                      Custom Top-Up Amount
                    </h3>
                  </div>
                  <p className="text-xs text-[#9CA3AF] mb-4">
                    Enter any custom INR value to recharge precise credits.
                  </p>

                  {/* Preset Amount Chips */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {PRESET_AMOUNTS.map((amt) => {
                      const isSelected = String(amountInr) === String(amt);
                      return (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setAmountInr(String(amt))}
                          className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-sm ${
                            isSelected
                              ? 'bg-[#2DD4BF]/20 border border-[#2DD4BF] text-[#62FAE3] shadow-[0_0_12px_rgba(45,212,191,0.2)]'
                              : 'bg-white/[0.03] hover:bg-white/[0.08] text-gray-300 border border-white/10'
                          }`}
                        >
                          ₹{amt.toLocaleString('en-IN')}
                        </button>
                      );
                    })}
                  </div>

                  {/* Input & Action */}
                  <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                    <div className="flex-1">
                      <Label
                        htmlFor="custom-amount-inr"
                        className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider font-mono mb-1 block"
                      >
                        Amount in INR (₹)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold font-sora text-sm">
                          ₹
                        </span>
                        <Input
                          id="custom-amount-inr"
                          type="number"
                          min={1}
                          step={1}
                          value={amountInr}
                          onChange={(e) => setAmountInr(e.target.value)}
                          className="pl-8 h-10 rounded-xl bg-black/60 border border-white/15 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] text-white font-sora text-sm"
                          placeholder="Enter INR amount"
                        />
                      </div>
                      <p className="text-[11px] text-[#9CA3AF] mt-1.5">
                        You will receive{' '}
                        <span className="text-[#62FAE3] font-bold font-mono">
                          {customCredits.toLocaleString('en-IN')} credits
                        </span>
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleCustomRecharge}
                      disabled={isPaying || !amountInr || Number(amountInr) <= 0}
                      className="h-10 px-6 rounded-xl font-bold text-xs tracking-wide bg-gradient-to-r from-[#8B5CF6] via-[#6366F1] to-[#2DD4BF] text-white hover:opacity-95 shadow-[0_0_18px_rgba(98,250,227,0.25)] transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                      data-testid="pricing-custom-recharge-btn"
                    >
                      {isPaying ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Opening Razorpay...</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-3.5 h-3.5" />
                          <span>Pay ₹{amountInr ? Number(amountInr).toLocaleString('en-IN') : '0'}</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* ─────────────────────────────────────────
                  Recent Transactions History
              ───────────────────────────────────────── */}
              {isLoggedIn && transactions.length > 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 sm:p-6 mb-12 shadow-xl font-jakarta">
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/[0.06]">
                    <h3 className="text-xs sm:text-sm font-bold font-sora text-white flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-[#2DD4BF]" />
                      Recent Wallet Activity
                    </h3>
                    <span className="text-[10px] font-mono text-[#9CA3AF]">
                      Last {transactions.length} events
                    </span>
                  </div>

                  <div className="divide-y divide-white/[0.04] max-h-60 overflow-y-auto pr-1">
                    {transactions.map((tx) => {
                      const isPositive = (tx.amount || 0) >= 0;
                      return (
                        <div
                          key={tx.id}
                          className="py-2.5 flex items-center justify-between gap-4 text-xs hover:bg-white/[0.02] px-2 rounded-lg transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-white truncate capitalize">
                              {tx.reason || tx.type || 'Credit adjustment'}
                            </p>
                            <p className="text-[10px] text-[#9CA3AF] font-mono">
                              {tx.created_at ? new Date(tx.created_at).toLocaleString() : 'Just now'}
                            </p>
                          </div>
                          <span
                            className={`font-mono font-bold text-xs shrink-0 px-2 py-0.5 rounded-full ${
                              isPositive
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                                : 'bg-white/[0.05] text-gray-300 border border-white/10'
                            }`}
                          >
                            {isPositive ? '+' : ''}
                            {Number(tx.amount || 0).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* ─────────────────────────────────────────
                  Trust & Assurance Guarantee Grid
              ───────────────────────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 font-jakarta">
                <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#2DD4BF]/15 border border-[#2DD4BF]/30 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-[#62FAE3]" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold font-sora text-white">
                      Instant Credit Delivery
                    </h4>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5 leading-relaxed">
                      Razorpay checkouts credit your wallet in seconds with automatic invoicing.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-[#D0BCFF]" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold font-sora text-white">
                      Zero Expiration Date
                    </h4>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5 leading-relaxed">
                      Purchased credits never expire. Use them whenever you create new content.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                    <RotateCcw className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold font-sora text-white">
                      Failed Job Guarantee
                    </h4>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5 leading-relaxed">
                      If an upstream AI model encounters an error, 0 credits are deducted.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}

export default PricingPage;
